// Types shared by the browser app and the Cloudflare Pages Functions.

export type Lang = "fr" | "es";
export const LANGS: Lang[] = ["fr", "es"];

export const LANG_NAMES: Record<Lang, string> = {
  fr: "French",
  es: "Spanish",
};

/** BCP-47 tags used to pick a speech-synthesis voice. */
export const LANG_LOCALES: Record<Lang, string> = {
  fr: "fr-FR",
  es: "es-ES",
};

export type NoteKind = "vocab" | "phrase" | "cloze";
export type Domain = "general" | "mining";
export type NoteStatus = "pending" | "active" | "rejected" | "deleted";
export type Direction = "recognition" | "production" | "cloze";
export type SourceKind = "podcast" | "document" | "capture" | "manual";

export interface Source {
  id: string;
  lang: Lang;
  kind: SourceKind;
  title: string;
  url: string | null;
  episode_date: string | null;
  body: string | null;
  created_at: number;
  updated_at: number;
}

export interface Note {
  id: string;
  lang: Lang;
  kind: NoteKind;
  /** Display form. Nouns always carry their article: "le gisement", "la mina". */
  term: string;
  lemma: string | null;
  translation: string;
  pos: string | null;
  gender: "m" | "f" | null;
  example: string | null;
  example_translation: string | null;
  cloze_text: string | null;
  cloze_answer: string | null;
  cloze_hint: string | null;
  note: string | null;
  domain: Domain;
  source_id: string | null;
  status: NoteStatus;
  created_at: number;
  updated_at: number;
}

export interface Card {
  id: string;
  note_id: string;
  lang: Lang;
  direction: Direction;
  due: number;
  stability: number;
  difficulty: number;
  elapsed_days: number;
  scheduled_days: number;
  learning_steps: number;
  reps: number;
  lapses: number;
  state: number;
  last_review: number | null;
  suspended: number;
  updated_at: number;
}

export interface Review {
  id: string;
  card_id: string;
  lang: Lang;
  rating: 1 | 2 | 3 | 4;
  state_before: number;
  reviewed_at: number;
  duration_ms: number | null;
}

/** A card joined to its note — what the review screen actually renders. */
export interface ReviewItem {
  card: Card;
  note: Note;
}

// --- Extraction -------------------------------------------------------------

/** One candidate produced by extraction, before it is approved into a note. */
export interface Candidate {
  kind: NoteKind;
  term: string;
  lemma: string | null;
  translation: string;
  pos: string | null;
  gender: "m" | "f" | null;
  example: string | null;
  example_translation: string | null;
  cloze_text: string | null;
  cloze_answer: string | null;
  cloze_hint: string | null;
  note: string | null;
  domain: Domain;
  /** Model's guess at CEFR level — used only to sort the approval list. */
  level: string | null;
}

export interface ExtractRequest {
  lang: Lang;
  /** Either a URL to fetch, or text pasted directly. */
  url?: string;
  text?: string;
  title?: string;
  sourceKind?: SourceKind;
  episodeDate?: string;
  /** Extra steer, e.g. "focus on the tailings section". */
  focus?: string;
}

export interface ExtractResponse {
  sourceTitle: string;
  sourceUrl: string | null;
  /** The text extraction actually ran over — stored with the source. */
  body: string;
  candidates: Candidate[];
  /** Lemmas already present in the collection, so the UI can grey them out. */
  duplicates: string[];
  usage: { input_tokens: number; output_tokens: number };
}

// --- Sync -------------------------------------------------------------------

export interface SyncPullResponse {
  now: number;
  sources: Source[];
  notes: Note[];
  cards: Card[];
  settings: Record<string, unknown>;
}

export interface SyncPushRequest {
  reviews: Review[];
  /** Card state after the client applied FSRS locally. */
  cards: Card[];
}

// --- Per-language study profile ---------------------------------------------

export interface LangProfile {
  /** New notes introduced per day. */
  newPerDay: number;
  /** Ceiling on cards shown in one session. */
  maxPerDay: number;
  /** FSRS target retention (0..1). Lower = longer intervals, more lapses. */
  requestRetention: number;
  /** Make a production card as well as a recognition card. */
  production: boolean;
  /** Show the production card first for a new note (recognition is easy here). */
  productionFirst: boolean;
  /**
   * Use FSRS learning steps (a card answered Good comes back in ~10 minutes
   * before graduating). Right for material being learned for the first time;
   * wrong for reactivating a language, where most cards are already known and
   * re-showing them inside the same session wastes the session.
   */
  shortTermSteps: boolean;
  /** Master switch for card audio. Off hides the speaker button entirely. */
  audioEnabled: boolean;
  /** Speak the term automatically when the card is shown. */
  autoplayAudio: boolean;
  /**
   * Chosen voice, by voiceURI. Null means take the best-scoring one available,
   * which is what you want across devices with very different voice sets.
   */
  voiceURI: string | null;
  /** Suspend and flag a card after this many lapses. */
  leechThreshold: number;
}

export const DEFAULT_PROFILES: Record<Lang, LangProfile> = {
  // French is a recall problem: the vocabulary is largely still there and needs
  // retrieval, not teaching. High volume, long intervals, production-weighted.
  fr: {
    newPerDay: 40,
    maxPerDay: 250,
    requestRetention: 0.87,
    production: true,
    productionFirst: true,
    shortTermSteps: false,
    audioEnabled: true,
    autoplayAudio: false,
    voiceURI: null,
    leechThreshold: 8,
  },
  // Spanish is an acquisition problem: low volume, audio on every card,
  // recognition before production.
  es: {
    newPerDay: 10,
    maxPerDay: 120,
    requestRetention: 0.9,
    production: true,
    productionFirst: false,
    shortTermSteps: true,
    audioEnabled: true,
    autoplayAudio: true,
    voiceURI: null,
    leechThreshold: 5,
  },
};
